import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, LineChart, Line, CartesianGrid, Legend } from "recharts";
import { Plus, Pencil, Trash2, Package, ShoppingCart, Users, IndianRupee, Ticket, Star, MessageSquare, Download, ChevronDown, Mail, Receipt, UserPlus } from "lucide-react";
import { AnalyticsRange, ChangeBadge, PRESETS } from "../components/admin/AnalyticsRange";
import { ImageUploader } from "../components/ImageUploader";
import AdminContent from "../components/admin/AdminContent";
import AdminStoreSettings from "../components/admin/AdminStoreSettings";
import { toast } from "sonner";

const EMPTY = {
  name: "", category: "T-Shirts", gender: "Men", price: 0, original_price: 0,
  description: "", fabric: "", material: "", fit: "Regular", stock: 100, featured: false,
  images: [""], sizes: ["S", "M", "L", "XL", "XXL"], colors: ["Black"],
};
const EMPTY_COUPON = {
  code: "", type: "percent", value: 10, desc: "", max: 0, min: 0,
  active: true, first_order_only: false, auto_apply: false,
  max_uses: 0, per_user_limit: 0, valid_from: "", valid_until: "",
};
const CATS = ["T-Shirts", "Shirts", "Pants", "Tops", "Bottoms", "Dresses", "Hoodies"];
const GENDERS = ["Men", "Women", "Unisex"];
const ORDER_STATUSES = ["pending", "confirmed", "shipped", "delivered", "cancelled"];

const COURIERS = ["Delhivery", "Blue Dart", "DTDC", "Xpressbees", "Ecom Express", "Shiprocket", "India Post", "Aramex", "DHL", "FedEx"];

function OrderTracking({ order, onSave, onEmail }) {
  const [courier, setCourier] = useState(order.courier || "");
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [link, setLink] = useState(order.tracking_url || "");
  return (
    <div>
      <div className="font-semibold mb-2">Courier & tracking</div>
      <div className="space-y-2">
        <Input list="courier-list" value={courier} onChange={(e) => setCourier(e.target.value)} placeholder="Courier (e.g. Delhivery)" className="h-9 text-xs" data-testid={`order-courier-${order.id}`} />
        <datalist id="courier-list">{COURIERS.map((c) => <option key={c} value={c} />)}</datalist>
        <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Tracking number" className="h-9 text-xs" data-testid={`order-tracking-${order.id}`} />
        <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Custom tracking link (optional, https://)" className="h-9 text-xs" data-testid={`order-tracking-url-${order.id}`} />
        <div className="flex flex-wrap gap-2">
          <Button size="sm" className="h-8 bg-[#1D3557] hover:bg-[#162a47] text-xs" onClick={() => onSave(order.id, courier, tracking, link)} data-testid={`order-save-tracking-${order.id}`}>Save tracking</Button>
          <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!order.courier || !order.tracking_number} onClick={() => onEmail(order.id)} data-testid={`order-send-tracking-email-${order.id}`}>
            <Mail size={13} className="mr-1" /> {order.tracking_emailed_at ? "Resend tracking email" : "Send tracking email"}
          </Button>
        </div>
        <p className="text-[11px] text-slate-400" data-testid={`order-tracking-email-status-${order.id}`}>
          {order.tracking_emailed_at ? `Emailed to customer on ${order.tracking_emailed_at.slice(0, 16).replace("T", " ")}` : "Auto-emails the customer when status is set to shipped."}
        </p>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, authChecked } = useApp();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [coupons, setCoupons] = useState([]);
  const [returns, setReturns] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [orderSearch, setOrderSearch] = useState("");
  const [orderFilter, setOrderFilter] = useState("all");
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [couponDialogOpen, setCouponDialogOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [couponForm, setCouponForm] = useState(EMPTY_COUPON);
  const [preset, setPreset] = useState("30d");
  const [range, setRange] = useState(() => PRESETS[1].range());

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/admin/stats", { params: range }).then(({ data }) => setStats(data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load analytics"));
  }, [user, range]);

  useEffect(() => {
    if (authChecked && (!user || user.role !== "admin")) {
      navigate("/");
    }
  }, [authChecked, user, navigate]);

  const loadAll = () => {
    api.get("/admin/stats", { params: range }).then(({ data }) => setStats(data)).catch(() => {});
    api.get("/products").then(({ data }) => setProducts(data));
    api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/admin/customers").then(({ data }) => setCustomers(data)).catch(() => {});
    api.get("/admin/coupons").then(({ data }) => setCoupons(data)).catch(() => {});
    api.get("/admin/returns").then(({ data }) => setReturns(data)).catch(() => {});
    api.get("/admin/reviews").then(({ data }) => setReviews(data)).catch(() => {});
  };

  useEffect(() => {
    if (user?.role === "admin") loadAll();
  }, [user]);

  if (!user || user.role !== "admin") return null;

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      ...p,
      images: p.images?.length ? p.images : [""],
      sizes: p.sizes || [],
      colors: p.colors || [],
    });
    setDialogOpen(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      price: Number(form.price),
      original_price: Number(form.original_price) || null,
      stock: Number(form.stock),
      images: form.images.filter(Boolean),
      colors: typeof form.colors === "string" ? form.colors.split(",").map((s) => s.trim()) : form.colors,
    };
    try {
      if (editing) {
        await api.put(`/products/${editing}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      setDialogOpen(false);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Product deleted");
    loadAll();
  };

  const updateOrderStatus = async (id, status) => {
    const { data } = await api.put(`/admin/orders/${id}/status`, { status });
    toast.success(data.tracking_emailed ? "Order shipped — tracking emailed to customer" : "Order updated");
    loadAll();
  };

  const openNewCoupon = () => {
    setEditingCoupon(null);
    setCouponForm(EMPTY_COUPON);
    setCouponDialogOpen(true);
  };
  const openEditCoupon = (c) => {
    setEditingCoupon(c.code);
    setCouponForm({ ...EMPTY_COUPON, ...c, valid_from: c.valid_from || "", valid_until: c.valid_until || "" });
    setCouponDialogOpen(true);
  };
  const saveCoupon = async () => {
    const payload = {
      ...couponForm,
      code: (couponForm.code || "").trim().toUpperCase(),
      value: Number(couponForm.value),
      max: Number(couponForm.max) || 0,
      min: Number(couponForm.min) || 0,
      max_uses: parseInt(couponForm.max_uses, 10) || 0,
      per_user_limit: parseInt(couponForm.per_user_limit, 10) || 0,
      valid_from: couponForm.valid_from || null,
      valid_until: couponForm.valid_until || null,
    };
    delete payload.used_count;
    delete payload.schedule_state;
    delete payload.id;
    if (!payload.code) { toast.error("Coupon code is required"); return; }
    try {
      if (editingCoupon) {
        await api.put(`/admin/coupons/${editingCoupon}`, payload);
        toast.success("Coupon updated");
      } else {
        await api.post("/admin/coupons", payload);
        toast.success("Coupon created");
      }
      setCouponDialogOpen(false);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const delCoupon = async (code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    await api.delete(`/admin/coupons/${code}`);
    toast.success("Coupon deleted");
    loadAll();
  };

  const statCards = stats?.period
    ? [
        { key: "revenue", icon: IndianRupee, label: "Revenue (INR)", value: `₹${Math.round(stats.period.revenue).toLocaleString("en-IN")}`, color: "text-emerald-600 bg-emerald-50" },
        { key: "orders", icon: ShoppingCart, label: "Paid Orders", value: stats.period.orders, color: "text-[#E63946] bg-red-50" },
        { key: "aov", icon: Receipt, label: "Avg. Order Value", value: `₹${Math.round(stats.period.aov).toLocaleString("en-IN")}`, color: "text-[#1D3557] bg-blue-50" },
        { key: "new_customers", icon: UserPlus, label: "New Customers", value: stats.period.new_customers, color: "text-[#D4AF37] bg-amber-50" },
      ]
    : [];

  const chartData = (stats?.recent_orders || []).flatMap((o) =>
    o.items.map((it) => ({ name: it.name.slice(0, 12), qty: it.quantity }))
  ).slice(0, 8);

  const RETURN_STATUSES = ["requested", "approved", "picked_up", "refunded", "completed", "rejected"];
  const updateReturn = async (id, status) => {
    try {
      await api.put(`/admin/returns/${id}`, { status });
      toast.success(`Request marked ${status}`);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const updatePickupDate = async (id, status, pickup_date) => {
    try {
      await api.put(`/admin/returns/${id}`, { status, pickup_date });
      toast.success("Pickup date updated");
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const moderateReview = async (id, status) => {
    try {
      await api.put(`/admin/reviews/${id}`, { status });
      toast.success(status === "approved" ? "Review approved" : "Review hidden");
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    await api.delete(`/admin/reviews/${id}`);
    toast.success("Review deleted");
    loadAll();
  };
  const pendingReviews = reviews.filter((r) => r.status === "pending").length;

  // Order management: search, filter, CSV, tracking
  const filteredOrders = orders.filter((o) => {
    const q = orderSearch.trim().toLowerCase();
    const matchesQ = !q || o.id.toLowerCase().includes(q) || (o.user_email || "").toLowerCase().includes(q);
    const matchesS = orderFilter === "all" || o.status === orderFilter;
    return matchesQ && matchesS;
  });
  const saveTracking = async (id, courier, tracking_number, tracking_url) => {
    try {
      const { data } = await api.put(`/admin/orders/${id}/status`, { courier, tracking_number, tracking_url });
      toast.success(data.tracking_emailed ? "Tracking saved & emailed to customer" : "Tracking saved");
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const emailTracking = async (id) => {
    try {
      const { data } = await api.post(`/admin/orders/${id}/send-tracking`);
      toast.success(`Tracking email sent to ${data.to}`);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const exportOrdersCSV = () => {
    const rows = [["Order ID", "Date", "Customer", "Items", "Amount", "Currency", "Payment", "Status", "Courier", "Tracking"]];
    filteredOrders.forEach((o) => {
      rows.push([
        o.id, (o.created_at || "").slice(0, 10), o.user_email,
        o.items.map((i) => `${i.name} x${i.quantity}`).join("; "),
        Math.round(o.amount), o.currency, o.payment_status, o.status,
        o.courier || "", o.tracking_number || "",
      ]);
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bull-orders-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Admin Dashboard</h1>
          <p className="text-sm text-slate-400">Manage products, orders & inventory</p>
        </div>
        <Button onClick={openNew} className="bg-[#E63946] hover:bg-[#d12836]" data-testid="admin-add-product-button">
          <Plus size={18} className="mr-1" /> Add Product
        </Button>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="mb-6 flex flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="admin-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="products" data-testid="admin-tab-products">Products</TabsTrigger>
          <TabsTrigger value="orders" data-testid="admin-tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="customers" data-testid="admin-tab-customers">Bull Family</TabsTrigger>
          <TabsTrigger value="coupons" data-testid="admin-tab-coupons">Coupons</TabsTrigger>
          <TabsTrigger value="returns" data-testid="admin-tab-returns">Returns</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="admin-tab-reviews" className="relative">
            Reviews
            {pendingReviews > 0 && <span className="ml-1.5 inline-flex items-center justify-center text-[10px] font-bold bg-[#E63946] text-white rounded-full px-1.5 h-4 min-w-4">{pendingReviews}</span>}
          </TabsTrigger>
          <TabsTrigger value="content" data-testid="admin-tab-content">Content</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div>
              <h2 className="font-display text-lg font-bold">Sales analytics</h2>
              <p className="text-xs text-slate-400" data-testid="analytics-range-label">
                {stats?.range ? `${stats.range.start} → ${stats.range.end} (${stats.range.days} days) vs ${stats.range.prev_start} → ${stats.range.prev_end}` : "Loading…"}
              </p>
            </div>
            <AnalyticsRange preset={preset} onChange={(k, r) => { setPreset(k); setRange(r); }} />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {statCards.map((c) => (
              <div key={c.key} className="p-5 rounded-2xl border bg-white dark:bg-slate-900" data-testid={`kpi-${c.key}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${c.color}`}>
                    <c.icon size={20} />
                  </div>
                  <ChangeBadge value={stats.change?.[c.key]} testId={`kpi-change-${c.key}`} />
                </div>
                <div className="text-2xl font-display font-extrabold" data-testid={`kpi-value-${c.key}`}>{c.value}</div>
                <div className="text-xs text-slate-400">{c.label}</div>
              </div>
            ))}
          </div>
          {stats && (
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500 mb-8" data-testid="store-totals">
              <span className="flex items-center gap-1"><IndianRupee size={12} /> All-time revenue <b className="text-slate-700 dark:text-slate-200">₹{Math.round(stats.revenue_inr).toLocaleString("en-IN")}</b></span>
              <span className="flex items-center gap-1"><ShoppingCart size={12} /> Total orders <b className="text-slate-700 dark:text-slate-200">{stats.total_orders}</b></span>
              <span className="flex items-center gap-1"><Package size={12} /> Products <b className="text-slate-700 dark:text-slate-200">{stats.total_products}</b></span>
              <span className="flex items-center gap-1"><Users size={12} /> Customers <b className="text-slate-700 dark:text-slate-200">{stats.customers}</b></span>
            </div>
          )}
          <div className="grid lg:grid-cols-3 gap-4 mb-4">
            <div className="lg:col-span-2 p-6 rounded-2xl border bg-white dark:bg-slate-900" data-testid="revenue-chart">
              <h3 className="font-display font-bold mb-4">Revenue (₹) — current vs previous period</h3>
              {(stats?.revenue_by_day || []).length === 0 ? (
                <p className="text-slate-400 text-sm py-10 text-center">No sales data yet.</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={stats.revenue_by_day}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={16} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" name="This period" dataKey="revenue" stroke="#E63946" strokeWidth={2.5} dot={false} />
                    <Line type="monotone" name="Previous period" dataKey="previous" stroke="#94A3B8" strokeWidth={1.5} strokeDasharray="5 4" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="p-6 rounded-2xl border bg-white dark:bg-slate-900">
              <h3 className="font-display font-bold mb-3">Orders by status <span className="text-xs font-normal text-slate-400">(in range)</span></h3>
              <div className="space-y-2">
                {Object.entries(stats?.orders_by_status || {}).map(([s, n]) => (
                  <div key={s} className="flex items-center justify-between text-sm">
                    <span className="capitalize text-slate-500">{s}</span>
                    <span className="font-bold">{n}</span>
                  </div>
                ))}
                {Object.keys(stats?.orders_by_status || {}).length === 0 && <p className="text-slate-400 text-sm">No orders yet.</p>}
              </div>
              <div className="mt-4 pt-4 border-t">
                <div className="text-xs text-slate-400">New customers (7 days)</div>
                <div className="text-2xl font-display font-extrabold text-[#E63946]">{stats?.new_customers_7d ?? 0}</div>
              </div>
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="p-6 rounded-2xl border bg-white dark:bg-slate-900">
              <h3 className="font-display font-bold mb-4">Best-selling products <span className="text-xs font-normal text-slate-400">(in range)</span></h3>
              {(stats?.top_products || []).length === 0 ? (
                <p className="text-slate-400 text-sm py-6 text-center">No sales yet.</p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={(stats.top_products || []).map((p) => ({ name: p.name.slice(0, 14), qty: p.qty }))} layout="vertical" margin={{ left: 20 }}>
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="qty" fill="#1D3557" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="p-6 rounded-2xl border bg-white dark:bg-slate-900">
              <h3 className="font-display font-bold mb-4 flex items-center gap-2">Low stock alerts {(stats?.low_stock || []).length > 0 && <span className="text-xs font-bold bg-red-100 text-red-600 rounded-full px-2 py-0.5">{stats.low_stock.length}</span>}</h3>
              {(stats?.low_stock || []).length === 0 ? (
                <p className="text-emerald-600 text-sm py-6 text-center">All products are well stocked ✓</p>
              ) : (
                <div className="space-y-2 max-h-[240px] overflow-y-auto">
                  {stats.low_stock.map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border-b border-dashed pb-1.5">
                      <span className="line-clamp-1 max-w-[220px]">{p.name}</span>
                      <span className={`font-bold ${p.stock === 0 ? "text-red-600" : "text-amber-600"}`}>{p.stock === 0 ? "Out of stock" : `${p.stock} left`}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="products">
          <div className="rounded-2xl border bg-white dark:bg-slate-900 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800 text-left">
                  <tr>
                    <th className="p-3 font-semibold">Product</th>
                    <th className="p-3 font-semibold">Category</th>
                    <th className="p-3 font-semibold">Price</th>
                    <th className="p-3 font-semibold">Stock</th>
                    <th className="p-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-t" data-testid={`admin-product-row-${p.id}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <img src={p.images?.[0]} alt="" className="w-9 h-11 object-cover rounded" />
                          <span className="font-medium line-clamp-1 max-w-[180px]">{p.name}</span>
                        </div>
                      </td>
                      <td className="p-3 text-slate-500">{p.gender} / {p.category}</td>
                      <td className="p-3 font-mono-price">₹{p.price}</td>
                      <td className="p-3">{p.stock}</td>
                      <td className="p-3">
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => openEdit(p)} className="p-1.5 rounded-md hover:bg-slate-100 text-[#1D3557]" data-testid={`admin-edit-${p.id}`}>
                            <Pencil size={16} />
                          </button>
                          <button onClick={() => del(p.id)} className="p-1.5 rounded-md hover:bg-red-50 text-red-500" data-testid={`admin-delete-${p.id}`}>
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="orders">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Input value={orderSearch} onChange={(e) => setOrderSearch(e.target.value)} placeholder="Search by order ID or email" className="max-w-xs" data-testid="order-search" />
            <Select value={orderFilter} onValueChange={setOrderFilter}>
              <SelectTrigger className="w-40" data-testid="order-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {ORDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-sm text-slate-400">{filteredOrders.length} order{filteredOrders.length !== 1 ? "s" : ""}</span>
            <Button variant="outline" size="sm" onClick={exportOrdersCSV} className="ml-auto" data-testid="order-export-csv"><Download size={15} className="mr-1.5" /> Export CSV</Button>
          </div>
          <div className="space-y-3" data-testid="admin-orders-list">
            {filteredOrders.length === 0 ? (
              <p className="text-slate-400 text-center py-10">No orders match.</p>
            ) : (
              filteredOrders.map((o) => (
                <div key={o.id} className="rounded-2xl border bg-white dark:bg-slate-900" data-testid={`admin-order-${o.id}`}>
                  <div className="p-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="cursor-pointer" onClick={() => setExpandedOrder(expandedOrder === o.id ? null : o.id)}>
                      <div className="font-semibold text-sm flex items-center gap-1">#{o.id.slice(-8).toUpperCase()} <ChevronDown size={14} className={`transition-transform ${expandedOrder === o.id ? "rotate-180" : ""}`} /></div>
                      <div className="text-xs text-slate-400">{o.user_email} • {o.items.length} items • {(o.created_at || "").slice(0, 10)}</div>
                    </div>
                    <div className="font-mono-price font-bold">
                      {o.currency === "INR" ? `₹${Math.round(o.amount)}` : `${o.currency} ${o.amount.toFixed(2)}`}
                    </div>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${o.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {o.payment_status}
                    </span>
                    <Select value={o.status} onValueChange={(v) => updateOrderStatus(o.id, v)}>
                      <SelectTrigger className="w-36" data-testid={`admin-order-status-${o.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ORDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {expandedOrder === o.id && (
                    <div className="border-t p-4 grid md:grid-cols-3 gap-4 text-sm" data-testid={`order-detail-${o.id}`}>
                      <div>
                        <div className="font-semibold mb-2">Items</div>
                        {o.items.map((it, i) => (
                          <div key={i} className="flex gap-2 items-center mb-2">
                            <img src={it.image} alt="" className="w-8 h-10 object-cover rounded" />
                            <span className="text-xs">{it.name} ×{it.quantity}{it.size ? ` • ${it.size}` : ""}</span>
                          </div>
                        ))}
                      </div>
                      <div>
                        <div className="font-semibold mb-2">Customer & shipping</div>
                        <div className="text-xs text-slate-500 space-y-0.5">
                          <div>{o.shipping?.name || o.user_email}</div>
                          {o.shipping?.phone && <div>{o.shipping.phone}</div>}
                          {o.shipping?.address && <div>{o.shipping.address}</div>}
                          {(o.shipping?.city || o.shipping?.pincode) && <div>{o.shipping?.city} {o.shipping?.pincode}</div>}
                          {o.coupon_code && <div className="text-[#E63946]">Coupon: {o.coupon_code}</div>}
                        </div>
                      </div>
                      <OrderTracking key={`${o.courier}-${o.tracking_number}-${o.tracking_url}`} order={o} onSave={saveTracking} onEmail={emailTracking} />
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </TabsContent>
        <TabsContent value="customers">
          <div className="rounded-2xl border bg-white dark:bg-slate-900 overflow-hidden" data-testid="admin-customers-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800 text-left">
                  <tr>
                    <th className="p-3 font-semibold">Customer</th>
                    <th className="p-3 font-semibold">Tier</th>
                    <th className="p-3 font-semibold">Points</th>
                    <th className="p-3 font-semibold">Wallet</th>
                    <th className="p-3 font-semibold">Spent</th>
                    <th className="p-3 font-semibold">Referrals</th>
                    <th className="p-3 font-semibold">Code</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.length === 0 ? (
                    <tr><td colSpan={7} className="p-6 text-center text-slate-400">No customers yet.</td></tr>
                  ) : customers.map((c) => (
                    <tr key={c.id} className="border-t" data-testid={`admin-customer-row-${c.id}`}>
                      <td className="p-3">
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-slate-400">{c.email}</div>
                      </td>
                      <td className="p-3">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${c.tier === "Gold" ? "bg-amber-100 text-amber-700" : c.tier === "Silver" ? "bg-slate-200 text-slate-700" : "bg-orange-100 text-orange-800"}`}>
                          {c.tier}
                        </span>
                      </td>
                      <td className="p-3 font-mono-price">{c.points}</td>
                      <td className="p-3 font-mono-price">₹{c.wallet_balance}</td>
                      <td className="p-3 font-mono-price">₹{c.total_spent}</td>
                      <td className="p-3 text-center">{c.referrals}</td>
                      <td className="p-3 font-mono-price text-xs text-slate-500">{c.referral_code}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="coupons">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display text-xl font-bold flex items-center gap-2"><Ticket className="h-5 w-5 text-[#E63946]" /> Discount Coupons</h2>
              <p className="text-sm text-slate-400">Create and manage codes shoppers can redeem at checkout.</p>
            </div>
            <Button onClick={openNewCoupon} className="bg-[#E63946] hover:bg-[#d12836]" data-testid="admin-add-coupon">
              <Plus className="h-4 w-4 mr-1" /> Add Coupon
            </Button>
          </div>
          <div className="rounded-2xl border bg-white dark:bg-slate-900 overflow-hidden" data-testid="admin-coupons-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800 text-left">
                  <tr>
                    <th className="p-3 font-semibold">Code</th>
                    <th className="p-3 font-semibold">Discount</th>
                    <th className="p-3 font-semibold">Min order</th>
                    <th className="p-3 font-semibold">Description</th>
                    <th className="p-3 font-semibold">Usage</th>
                    <th className="p-3 font-semibold">Validity</th>
                    <th className="p-3 font-semibold">Flags</th>
                    <th className="p-3 font-semibold">Status</th>
                    <th className="p-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.length === 0 ? (
                    <tr><td colSpan={9} className="p-6 text-center text-slate-400">No coupons yet.</td></tr>
                  ) : coupons.map((c) => (
                    <tr key={c.code} className="border-t" data-testid={`admin-coupon-row-${c.code}`}>
                      <td className="p-3 font-mono-price font-bold">{c.code}</td>
                      <td className="p-3">{c.type === "percent" ? `${c.value}%${c.max ? ` (max ₹${c.max})` : ""}` : `₹${c.value}`}</td>
                      <td className="p-3 font-mono-price">{c.min ? `₹${c.min}` : "—"}</td>
                      <td className="p-3 text-slate-500 max-w-[220px] truncate">{c.desc}</td>
                      <td className="p-3 text-xs" data-testid={`admin-coupon-usage-${c.code}`}>
                        <div className="font-mono-price font-bold">{c.used_count ?? 0}{c.max_uses ? ` / ${c.max_uses}` : ""}</div>
                        <div className="text-slate-400 mt-0.5">• {c.per_user_limit ? `${c.per_user_limit}× per shopper` : "No per-shopper cap"}</div>
                      </td>
                      <td className="p-3 text-xs" data-testid={`admin-coupon-validity-${c.code}`}>
                        <div className="text-slate-500">{c.valid_from || c.valid_until ? `${c.valid_from || "Now"} → ${c.valid_until || "No end"}` : "Always"}</div>
                        {c.schedule_state === "scheduled" && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">SCHEDULED</span>}
                        {c.schedule_state === "expired" && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">EXPIRED</span>}
                        {c.max_uses > 0 && c.used_count >= c.max_uses && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600 ml-1">LIMIT HIT</span>}
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {c.auto_apply && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">AUTO</span>}
                          {c.first_order_only && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">1ST ORDER</span>}
                        </div>
                      </td>
                      <td className="p-3">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${c.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
                          {c.active ? "Active" : "Disabled"}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button size="icon" variant="ghost" onClick={() => openEditCoupon(c)} data-testid={`admin-edit-coupon-${c.code}`}><Pencil className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => delCoupon(c.code)} data-testid={`admin-delete-coupon-${c.code}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="returns">
          <div className="rounded-2xl border bg-white dark:bg-slate-900 overflow-hidden" data-testid="admin-returns-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800 text-left">
                  <tr>
                    <th className="p-3 font-semibold">Item</th>
                    <th className="p-3 font-semibold">Type</th>
                    <th className="p-3 font-semibold">Reason</th>
                    <th className="p-3 font-semibold">Refund</th>
                    <th className="p-3 font-semibold">Customer</th>
                    <th className="p-3 font-semibold">Pickup</th>
                    <th className="p-3 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {returns.length === 0 ? (
                    <tr><td colSpan={7} className="p-6 text-center text-slate-400">No return/exchange requests yet.</td></tr>
                  ) : returns.map((r) => (
                    <tr key={r.id} className="border-t align-top" data-testid={`admin-return-row-${r.id}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <img src={r.item?.image} alt="" className="w-10 h-12 object-cover rounded" />
                          <div>
                            <div className="font-medium line-clamp-1 max-w-[160px]">{r.item?.name}</div>
                            <div className="text-xs text-slate-400">#{r.order_number} • {r.item?.size || "-"}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-3">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${r.type === "exchange" ? "bg-indigo-100 text-indigo-700" : "bg-amber-100 text-amber-700"}`}>{r.type}</span>
                        {r.type === "exchange" && (r.exchange_size || r.exchange_color) && (
                          <div className="text-xs text-slate-400 mt-1">→ {r.exchange_size} {r.exchange_color}</div>
                        )}
                      </td>
                      <td className="p-3 text-slate-500 max-w-[200px]">{r.reason}</td>
                      <td className="p-3">
                        {r.type === "return" ? (
                          <div>
                            <div className="font-mono-price">₹{r.refund_amount}</div>
                            <div className="text-xs text-slate-400">{r.refund_method === "wallet" ? "Wallet" : "Original"}{r.refund_credited ? " • credited" : ""}</div>
                          </div>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="p-3 text-xs text-slate-500">{r.user_email}</td>
                      <td className="p-3">
                        <input type="date" defaultValue={r.pickup_date || ""} onChange={(e) => updatePickupDate(r.id, r.status, e.target.value)} className="text-xs border rounded px-2 py-1 bg-transparent" data-testid={`admin-return-pickup-${r.id}`} />
                      </td>
                      <td className="p-3">
                        <Select value={r.status} onValueChange={(v) => updateReturn(r.id, v)}>
                          <SelectTrigger className="w-[150px]" data-testid={`admin-return-status-${r.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {RETURN_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="reviews">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display text-xl font-bold flex items-center gap-2"><MessageSquare className="h-5 w-5 text-[#E63946]" /> Customer Reviews</h2>
              <p className="text-sm text-slate-400">Approve reviews to show them on the product page, or hide the ones you don't want.</p>
            </div>
            {pendingReviews > 0 && <span className="text-sm font-semibold text-[#E63946]">{pendingReviews} awaiting review</span>}
          </div>
          <div className="space-y-3" data-testid="admin-reviews-list">
            {reviews.length === 0 ? (
              <div className="rounded-2xl border bg-white dark:bg-slate-900 p-8 text-center text-slate-400">No reviews yet.</div>
            ) : reviews.map((r) => (
              <div key={r.id} className="rounded-2xl border bg-white dark:bg-slate-900 p-4 flex flex-col sm:flex-row gap-4" data-testid={`admin-review-${r.id}`}>
                <img src={r.product_image} alt="" className="w-14 h-16 object-cover rounded-lg shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{r.product_name}</span>
                    <span className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={13} className={n <= r.rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-slate-300"} />)}
                    </span>
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${r.status === "approved" ? "bg-emerald-100 text-emerald-700" : r.status === "hidden" ? "bg-slate-200 text-slate-500" : "bg-amber-100 text-amber-700"}`}>{r.status}</span>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">{r.comment || <span className="italic text-slate-400">No comment</span>}</p>
                  <div className="text-xs text-slate-400 mt-1">by {r.user_name} • {new Date(r.created_at).toLocaleDateString()}</div>
                </div>
                <div className="flex sm:flex-col gap-2 shrink-0">
                  {r.status !== "approved" && (
                    <Button size="sm" onClick={() => moderateReview(r.id, "approved")} className="bg-emerald-600 hover:bg-emerald-700 text-xs h-8" data-testid={`review-approve-${r.id}`}>Approve</Button>
                  )}
                  {r.status !== "hidden" && (
                    <Button size="sm" variant="outline" onClick={() => moderateReview(r.id, "hidden")} className="text-xs h-8" data-testid={`review-hide-${r.id}`}>Hide</Button>
                  )}
                  <Button size="icon" variant="ghost" onClick={() => deleteReview(r.id)} className="h-8 w-8" data-testid={`review-delete-${r.id}`}><Trash2 size={14} className="text-red-500" /></Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="content">
          <AdminContent />
        </TabsContent>

        <TabsContent value="settings">
          <AdminStoreSettings />
        </TabsContent>
      </Tabs>

      {/* coupon dialog */}
      <Dialog open={couponDialogOpen} onOpenChange={setCouponDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-coupon-dialog">
          <DialogHeader>
            <DialogTitle>{editingCoupon ? "Edit Coupon" : "Add Coupon"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Code</Label>
              <Input value={couponForm.code} onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })} placeholder="e.g. SUMMER15" data-testid="admin-coupon-code" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={couponForm.type} onValueChange={(v) => setCouponForm({ ...couponForm, type: v })}>
                  <SelectTrigger data-testid="admin-coupon-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percent (%)</SelectItem>
                    <SelectItem value="flat">Flat (₹)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{couponForm.type === "percent" ? "Value (%)" : "Value (₹)"}</Label><Input type="number" value={couponForm.value} onChange={(e) => setCouponForm({ ...couponForm, value: e.target.value })} data-testid="admin-coupon-value" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Max discount ₹ {couponForm.type === "percent" ? "" : "(optional)"}</Label><Input type="number" value={couponForm.max} onChange={(e) => setCouponForm({ ...couponForm, max: e.target.value })} data-testid="admin-coupon-max" /></div>
              <div><Label>Min order ₹</Label><Input type="number" value={couponForm.min} onChange={(e) => setCouponForm({ ...couponForm, min: e.target.value })} data-testid="admin-coupon-min" /></div>
            </div>
            <div><Label>Description</Label><Input value={couponForm.desc} onChange={(e) => setCouponForm({ ...couponForm, desc: e.target.value })} placeholder="Shown to shoppers" data-testid="admin-coupon-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Total uses cap (0 = unlimited)</Label><Input type="number" min="0" value={couponForm.max_uses} onChange={(e) => setCouponForm({ ...couponForm, max_uses: e.target.value })} data-testid="admin-coupon-max-uses" /></div>
              <div><Label>Uses per shopper (0 = unlimited)</Label><Input type="number" min="0" value={couponForm.per_user_limit} onChange={(e) => setCouponForm({ ...couponForm, per_user_limit: e.target.value })} data-testid="admin-coupon-per-user" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Starts on (optional)</Label><Input type="date" value={couponForm.valid_from} onChange={(e) => setCouponForm({ ...couponForm, valid_from: e.target.value })} data-testid="admin-coupon-valid-from" /></div>
              <div><Label>Expires after (optional)</Label><Input type="date" value={couponForm.valid_until} onChange={(e) => setCouponForm({ ...couponForm, valid_until: e.target.value })} data-testid="admin-coupon-valid-until" /></div>
            </div>
            <div className="flex flex-col gap-2 pt-1">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={couponForm.active} onChange={(e) => setCouponForm({ ...couponForm, active: e.target.checked })} data-testid="admin-coupon-active" />
                Active (available at checkout)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={couponForm.first_order_only} onChange={(e) => setCouponForm({ ...couponForm, first_order_only: e.target.checked })} data-testid="admin-coupon-first-order" />
                First order only
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={couponForm.auto_apply} onChange={(e) => setCouponForm({ ...couponForm, auto_apply: e.target.checked })} data-testid="admin-coupon-auto" />
                Auto-apply for eligible shoppers at checkout
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCouponDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveCoupon} className="bg-[#E63946] hover:bg-[#d12836]" data-testid="admin-save-coupon">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-product-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Product" : "Add Product"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="admin-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Gender</Label>
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger data-testid="admin-form-gender"><SelectValue /></SelectTrigger>
                  <SelectContent>{GENDERS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Price ₹</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="admin-form-price" /></div>
              <div><Label>MRP ₹</Label><Input type="number" value={form.original_price} onChange={(e) => setForm({ ...form, original_price: e.target.value })} /></div>
              <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} /></div>
            </div>
            <div><Label>Product Photos</Label><ImageUploader images={form.images} onChange={(imgs) => setForm({ ...form, images: imgs.length ? imgs : [""] })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Fabric</Label><Input value={form.fabric} onChange={(e) => setForm({ ...form, fabric: e.target.value })} /></div>
              <div><Label>Colors (comma sep)</Label><Input value={Array.isArray(form.colors) ? form.colors.join(", ") : form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} /></div>
            </div>
            <div><Label>Material & Composition <span className="text-slate-400 font-normal text-xs">(e.g. 100% Combed Cotton, 180 GSM)</span></Label><Input value={form.material || ""} onChange={(e) => setForm({ ...form, material: e.target.value })} data-testid="admin-form-material" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="admin-form-featured" />
              Mark as Bestseller
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#E63946] hover:bg-[#d12836]" data-testid="admin-save-product">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
